import {useCallback, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import i18n from 'i18n-js';
import {
  deleteTenant,
  duplicateTenant,
  restoreTenant,
  setTestTenant
} from '../../../actions/adminTenantsActions';
import {refreshTenantSettings} from '../../../actions/adminToolsActions';

const gridText = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

export const DIALOGS = {
  delete: {
    message: name => gridText('deleteConfirm', {name}),
    label: () => gridText('delete'),
    danger: true
  },
  duplicate: {
    message: name => gridText('duplicatePrompt', {name}),
    label: () => gridText('duplicate'),
    withInput: true,
    inputMatches: () => value => value.trim() !== ''
  },
  restore: {
    message: () => gridText('restoreConfirm'),
    label: () => gridText('restore'),
    danger: true,
    withInput: true,
    inputMatches: name => value => value === name
  }
};

const useTenantActions = ({tenantId, tenantName, model, reload, notify}) => {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const confirming = useRef(false);
  const toggling = useRef(false);

  const row = {
    id: tenantId,
    name: model.name,
    scheduled_import_toggle: model.scheduledImportToggle,
    inventory_report_toggle: model.inventoryReportToggle
  };

  const openDialog = useCallback(kind => setDialog({kind}), []);
  const closeDialog = useCallback(() => setDialog(null), []);

  const confirmDialog = async value => {
    if (confirming.current) return;
    const {kind} = dialog;
    const thunks = {
      delete: () => deleteTenant(row),
      duplicate: () => duplicateTenant(row, value.trim()),
      restore: () => restoreTenant(row)
    };
    confirming.current = true;
    setBusy(true);
    const result = await dispatch(thunks[kind]());
    confirming.current = false;
    setBusy(false);
    setDialog(null);
    if (!result.ok) {
      notify(result.error, 'error');
      return;
    }
    notify(result.message || i18n.t('adminTenantPage.actionDone'), 'success');
    if (kind !== 'duplicate') reload();
  };

  const toggleTestTenant = async () => {
    if (toggling.current || model.testTenant === null) return;
    toggling.current = true;
    setTestBusy(true);
    const next = !model.testTenant;
    const result = await dispatch(setTestTenant(row, next));
    if (!result.ok) {
      notify(result.error, 'error');
      toggling.current = false;
      setTestBusy(false);
      return;
    }
    const reread = await dispatch(refreshTenantSettings(tenantId));
    toggling.current = false;
    setTestBusy(false);
    const held =
      reread.ok && reread.settings.tenant_settings
        ? reread.settings.tenant_settings.test_tenant_toggle
        : next;
    if (held !== next) {
      notify(i18n.t('adminTenantPage.didNotSave'), 'error');
      return;
    }
    notify(i18n.t('adminTenantPage.savedFor', {tenant: tenantName}), 'success');
  };

  return {
    dialog,
    busy,
    testBusy,
    openDialog,
    closeDialog,
    confirmDialog,
    toggleTestTenant
  };
};

export default useTenantActions;
